'use memo'

import type { ComponentType } from 'react'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  CustomizationIcon,
  EmailCustomizationIcon,
  GeneralSettingsIcon,
  IntegrationsIcon,
  PaymentsIcon,
  PlayerCustomFormIcon,
  TeamCustomFormIcon,
  WaiverIcon,
} from '../components/SidebarIcons'
import type { SidebarIconProps } from '../components/SidebarIcons'
import { useRegistrationSetupContext } from '../context'

type SidebarIconComponent = ComponentType<SidebarIconProps>

export type TabItem = {
  key: string
  label: string
  icon: SidebarIconComponent
  disabled: boolean
  disabledTooltip?: string
  /**
   * Placeholder row: the tab's visibility is still being resolved, so the slot
   * is reserved with a skeleton instead of popping in once the data lands.
   */
  loading?: boolean
}

const labelById: Record<number, string> = {
  0: 'General Settings',
  1: 'Payments / Fees',
  2: 'Player Waivers',
  3: 'Team Custom Form',
  4: 'Player Custom Form',
  5: 'Header Customization',
  6: 'Email Customization',
  7: 'Integrations',
}

const iconById: Record<number, SidebarIconComponent> = {
  0: GeneralSettingsIcon,
  1: PaymentsIcon,
  2: WaiverIcon,
  3: TeamCustomFormIcon,
  4: PlayerCustomFormIcon,
  5: CustomizationIcon,
  6: EmailCustomizationIcon,
  7: IntegrationsIcon,
}

export function useRegistrationSetupTabs(
  isLegacyRegistration: boolean
): TabItem[] {
  const { state, apiWaivers, apiAccount, isAccountLoading } =
    useRegistrationSetupContext()

  const waiverDisabled = !apiWaivers || apiWaivers.length === 0

  // The Integrations tab only exists when the account has Hockey Canada
  // switched on — it is the tab's only content today. Hockey Canada is a
  // Canlan-only integration.
  const hockeyCanadaEnabled =
    isCanlan && apiAccount?.integrations?.hockeycanada?.is_enabled === true
  const emailDisabled =
    !state.enableTeamRegistration && !state.enablePlayerRegistration

  const items: TabItem[] = [
    {
      key: '0',
      label: labelById[0],
      icon: iconById[0],
      disabled: false,
    },
    {
      key: '1',
      label: labelById[1],
      icon: iconById[1],
      disabled: false,
    },
    {
      key: '2',
      label: labelById[2],
      icon: iconById[2],
      disabled: waiverDisabled,
      disabledTooltip: waiverDisabled
        ? 'Waiver(s) are required for signature during registration. New waivers can be added via Account Settings.'
        : undefined,
    },
  ]

  if (!isLegacyRegistration && state.enableTeamRegistration) {
    items.push({
      key: '3',
      label: labelById[3],
      icon: iconById[3],
      disabled: false,
    })
  }

  if (!isLegacyRegistration && state.enablePlayerRegistration) {
    items.push({
      key: '4',
      label: labelById[4],
      icon: iconById[4],
      disabled: false,
    })
  }

  items.push({
    key: '5',
    label: labelById[5],
    icon: iconById[5],
    disabled: false,
  })

  items.push({
    key: '6',
    label: labelById[6],
    icon: iconById[6],
    disabled: emailDisabled,
  })

  // Reserve the Integrations slot while the account is still loading — the tab
  // may or may not survive, but a skeleton keeps it from popping the list.
  if (isAccountLoading) {
    items.push({
      key: '7',
      label: labelById[7],
      icon: iconById[7],
      disabled: true,
      loading: true,
    })
  } else if (hockeyCanadaEnabled) {
    items.push({
      key: '7',
      label: labelById[7],
      icon: iconById[7],
      disabled: false,
    })
  }

  return items
}
